/**
 * Incidentes de seguridad de los datos (brechas): qué se registra, cuánto tiempo hay para avisar y los borradores de los avisos.
 *
 * IMPORTANTE: el plazo para notificar a la Superintendencia de Protección de Datos Personales (SPDP) lo fija la ley. La guía normativa que se revisó
 * hablaba de 72 horas, pero esa cifra NO está verificada: el abogado debe confirmarla y se puede cambiar en el Panel Donna. Los textos de los avisos
 * son borradores: los revisa el abogado antes de enviarlos a una clínica, a la autoridad o a pacientes.
 */
export const TIPOS_INCIDENTE = {
  acceso_no_autorizado: "Acceso no autorizado a datos",
  perdida_datos: "Pérdida o destrucción de datos",
  filtracion: "Filtración o divulgación indebida de datos",
  malware: "Ataque informático (virus, ransomware)",
  error_humano: "Error humano (por ejemplo, un envío a la persona equivocada)",
  indisponibilidad: "Caída prolongada del servicio",
  otro: "Otro",
} as const;
export type TipoIncidente = keyof typeof TIPOS_INCIDENTE;

export const SEVERIDADES = { baja: "Baja", media: "Media", alta: "Alta", critica: "Crítica" } as const;
export type Severidad = keyof typeof SEVERIDADES;

export const ESTADOS_INCIDENTE = { abierto: "Abierto", contenido: "Contenido", resuelto: "Resuelto", cerrado: "Cerrado" } as const;
export type EstadoIncidente = keyof typeof ESTADOS_INCIDENTE;

export const CATEGORIAS_DATOS = {
  identificacion: "Datos de identificación (nombre, cédula o pasaporte, fecha de nacimiento)",
  contacto: "Datos de contacto (celular, correo, dirección)",
  salud: "Datos de salud (historia clínica, diagnósticos, recetas, odontograma)",
  imagenes: "Fotografías y radiografías",
  credenciales: "Contraseñas o claves de acceso",
  pagos: "Datos de pagos y facturación",
  otros: "Otros datos",
} as const;
export type CategoriaDatos = keyof typeof CATEGORIAS_DATOS;

export const ALCANCES = { todas: "Todas las clínicas", algunas: "Algunas clínicas", ninguna: "Ninguna clínica (solo Donna)" } as const;
export type Alcance = keyof typeof ALCANCES;

/** Horas para notificar a la autoridad desde que se detecta el incidente. VALOR PROVISIONAL: el abogado debe confirmar el plazo que fija la ley. */
export const PLAZO_HORAS_NOTIFICACION_POR_DEFECTO = 72;

export const limiteNotificacion = (detectadoEn: Date, horas: number) => new Date(detectadoEn.getTime() + horas * 3600000);
export const horasRestantes = (detectadoEn: Date, horas: number, ahora: Date = new Date()) => Math.round(((limiteNotificacion(detectadoEn, horas).getTime() - ahora.getTime()) / 3600000) * 10) / 10;

export type EstadoPlazo = "notificada" | "no_aplica" | "en_plazo" | "por_vencer" | "vencido";
/** Cómo va el plazo para avisar a la autoridad: ya avisada, no corresponde avisar, en plazo, a menos de 24 horas o vencido. */
export function estadoPlazo(spdp: "pendiente" | "notificada" | "no_aplica", restantes: number): EstadoPlazo {
  if (spdp === "notificada") return "notificada";
  if (spdp === "no_aplica") return "no_aplica";
  return restantes <= 0 ? "vencido" : restantes <= 24 ? "por_vencer" : "en_plazo";
}

export interface DatosIncidente {
  titulo: string; descripcion: string; tipo: TipoIncidente; detectadoEn: Date; ocurridoAprox: string; datosAfectados: readonly CategoriaDatos[];
  personasAprox: number | null; medidas: string;
}

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
/** «2 de noviembre de 2026 a las 14:30» en hora de Ecuador (UTC−5). */
export function fechaHoraEc(d: Date): string {
  const l = new Date(d.getTime() - 5 * 3600000);
  return `${l.getUTCDate()} de ${MESES[l.getUTCMonth()]} de ${l.getUTCFullYear()} a las ${String(l.getUTCHours()).padStart(2, "0")}:${String(l.getUTCMinutes()).padStart(2, "0")}`;
}
const lista = (cats: readonly CategoriaDatos[]) => (cats.length ? cats.map((c) => `- ${CATEGORIAS_DATOS[c]}`).join("\n") : "- Todavía se está determinando qué datos pudieron verse afectados.");
const personas = (n: number | null) => (n === null ? "Todavía no se conoce el número de personas afectadas." : `Número aproximado de personas afectadas: ${n}.`);

/**
 * Aviso de Donna (encargado del tratamiento) a la administración de una clínica (responsable del tratamiento). Es el aviso que la clínica necesita
 * para decidir si debe notificar a la SPDP y a sus pacientes. Se puede editar antes de enviarlo. Usa «{clinica}» si se quiere poner el nombre después.
 */
export function plantillaAvisoClinica(i: DatosIncidente, clinica: string, contacto: string): { asunto: string; texto: string } {
  return {
    asunto: `Aviso de incidente de seguridad en Donna: ${i.titulo}`,
    texto: `Estimado/a administrador/a de ${clinica}:

Le escribimos como proveedor del sistema Donna, en nuestra calidad de encargados del tratamiento de los datos de sus pacientes, para informarle de un incidente de seguridad.

QUÉ PASÓ
${i.descripcion}
Tipo de incidente: ${TIPOS_INCIDENTE[i.tipo]}.
Lo detectamos el ${fechaHoraEc(i.detectadoEn)} (hora de Ecuador).${i.ocurridoAprox ? ` Ocurrió aproximadamente: ${i.ocurridoAprox}.` : ""}

QUÉ DATOS PUEDEN HABERSE VISTO AFECTADOS
${lista(i.datosAfectados)}
${personas(i.personasAprox)}

QUÉ HEMOS HECHO
${i.medidas || "Estamos investigando el incidente y tomando medidas para contenerlo. Le informaremos de cualquier novedad."}

QUÉ PUEDE HACER USTED
Como responsable del tratamiento de los datos de sus pacientes, a usted le corresponde decidir si debe notificar el incidente a la Superintendencia de Protección de Datos Personales y a los pacientes afectados, según la Ley Orgánica de Protección de Datos Personales. Le recomendamos:
1. Conservar este aviso: es la constancia de cuándo y cómo se lo comunicamos.
2. Revisar los accesos de su personal a Donna y cambiar las contraseñas si lo considera necesario.
3. Estar atento a mensajes sospechosos que puedan recibir sus pacientes.
4. Escribirnos si necesita más información para su propia evaluación.

${contacto ? `Puede contactarnos en ${contacto}.` : "Puede responder a este correo."}

Lamentamos las molestias y le agradecemos su confianza.
El equipo de Donna`,
  };
}

/** Borrador de la notificación a la SPDP: lo que normalmente debe contener. Los datos entre corchetes los completa la empresa; el abogado lo revisa. */
export function borradorNotificacionSPDP(i: DatosIncidente, horasPlazo: number): string {
  return `BORRADOR PARA REVISIÓN DEL ABOGADO: NO ENVIAR SIN REVISAR

NOTIFICACIÓN DE VIOLACIÓN DE SEGURIDAD DE DATOS PERSONALES
A la Superintendencia de Protección de Datos Personales

1. DATOS DE QUIEN NOTIFICA
Razón social y RUC: [completar]
Calidad: encargado del tratamiento (proveedor del sistema Donna); los responsables son las clínicas y odontólogos usuarios.
Delegado de Protección de Datos o persona de contacto: [completar: nombre, correo y teléfono]

2. DESCRIPCIÓN DEL INCIDENTE
Título: ${i.titulo}
Tipo: ${TIPOS_INCIDENTE[i.tipo]}.
Fecha y hora de detección: ${fechaHoraEc(i.detectadoEn)} (hora de Ecuador).${i.ocurridoAprox ? `\nMomento aproximado en que ocurrió: ${i.ocurridoAprox}.` : ""}
Descripción: ${i.descripcion}

3. CATEGORÍAS DE DATOS Y DE TITULARES AFECTADOS
Titulares: pacientes de consultorios y clínicas odontológicas.
Datos:
${lista(i.datosAfectados)}
${personas(i.personasAprox)}

4. CONSECUENCIAS PROBABLES
[completar: por ejemplo, riesgo de suplantación de identidad, de divulgación de datos de salud o de mensajes engañosos a pacientes]

5. MEDIDAS ADOPTADAS Y PROPUESTAS
${i.medidas || "[completar]"}
Medidas para informar a los responsables (clínicas) y a los titulares: [completar]

6. PLAZO
El plazo de notificación considerado fue de ${horasPlazo} horas desde la detección (valor a confirmar con la ley vigente). Si se notifica fuera de ese plazo, explicar los motivos del retraso: [completar]

Fecha y firma: [completar]`;
}

/** Borrador del aviso que cada clínica (responsable) puede enviar a sus pacientes. */
export function borradorAvisoPacientes(i: DatosIncidente, clinica: string): string {
  return `BORRADOR PARA REVISIÓN DEL ABOGADO: NO ENVIAR SIN REVISAR

Estimado/a paciente:

Le escribimos de ${clinica || "[nombre de la clínica]"} para informarle que el ${fechaHoraEc(i.detectadoEn)} el proveedor de nuestro sistema de historias clínicas detectó un incidente de seguridad que pudo afectar algunos de sus datos personales.

Qué datos pudieron verse afectados:
${lista(i.datosAfectados)}

Qué estamos haciendo: ${i.medidas || "estamos trabajando con el proveedor para investigar lo ocurrido y reforzar la seguridad."}

Qué puede hacer usted: desconfíe de mensajes, llamadas o correos que le pidan datos personales o dinero y digan venir de nuestra clínica; ante la duda, comuníquese directamente con nosotros.

Si tiene preguntas o desea ejercer sus derechos sobre sus datos personales, puede escribirnos a [correo o teléfono de la clínica].

Lamentamos las molestias.
${clinica || "[Nombre de la clínica]"}`;
}
